import { FC, useEffect, useState } from 'react';
import { ProcessVariableCardProps, StatusProcessVariable } from './ProcessVariableCard.types';
import { AllieCardStyled } from './ProcessVariableCard.styles';
import { AllieColors } from '../../../common/color.types';
import { ContentProcessVariable } from './components/ContentProcessVariable';
import { VariableHeader } from './components/VariableHeader';
import { ContentExpandedVariable } from './components/ContentExpandedVariable';

const mapStatusColorBorderChip: Record<StatusProcessVariable | 'none', AllieColors> = {
  normal: 'success-main',
  outOfRange: 'warning-main',
  critical: 'error-main',
  outdated: 'neutral-60',
  none: 'neutral-60',
  stationStopped: 'neutral-50',
  machineStopped: 'neutral-50',
};

const mapStatusColorFont: Record<StatusProcessVariable | 'none', AllieColors> = {
  normal: 'success-main',
  outOfRange: 'warning-main',
  critical: 'error-main',
  outdated: 'neutral-70',
  none: 'neutral-70',
  stationStopped: 'neutral-50',
  machineStopped: 'neutral-50',
};

const mapStatusBackgroundColor: Record<StatusProcessVariable | 'none', AllieColors> = {
  normal: 'success-5',
  outOfRange: 'warning-5',
  critical: 'error-5',
  outdated: 'transparent',
  none: 'neutral-10',
  stationStopped: 'neutral-5',
  machineStopped: 'neutral-5',
};

export const ProcessVariableCard: FC<ProcessVariableCardProps> = ({
  id,
  name,
  statusText,
  value,
  range,
  status,
  lastChangeAt,
  graphTrendPreview,
  isFavorite,
  handleAddFavoriteVariable,
  handleDeleteFavoriteVariable,
  machineName,
  showFavButton,
  favoriteView,
  textOutdated,
  expanded,
  labelValues,
  isCustomType,
  isMobile,
  avgLabel,
  avgValue,
  minLabel,
  minValue,
  maxLabel,
  maxValue,
  withoutDataLabel,
  machineStoppedLabel,
  isMetricOnOff,
  showActionPlanButton,
  actionPlanButtonText,
  onClickActionPlanButton,
  tags,
  onEditTags,
  editTagsAriaLabel,
}) => {
  const borderChipColor = mapStatusColorBorderChip[status];
  const fontColor = mapStatusColorFont[status];
  const backgroundColor = mapStatusBackgroundColor[status];
  const [isLoading, setIsLoading] = useState(false);

  const [localIsFavorite, setLocalIsFavorite] = useState(isFavorite);

  useEffect(() => {
    setLocalIsFavorite(isFavorite);
  }, [isFavorite]);

  return (
    <AllieCardStyled
      style={{
        margin: 0,
        ...(!expanded && { border: '1px solid var(--allie-color-neutral-20)' }),
        boxShadow: 'none',
        display: 'flex',
        flexDirection: 'column',
      }}
      radius="md"
      header={{
        children: !expanded && (
          <VariableHeader
            {...{
              isLoading,
              setIsLoading,
              id,
              name,
              lastChangeAt,
              showFavButton,
              isFavorite: localIsFavorite,
              withoutDataLabel,
              machineStoppedLabel,
              tags,
              onEditTags,
              editTagsAriaLabel,
              handleDeleteFavoriteVariable: (
                variableId: number,
                variableName: string,
                onCompleted?: () => void
              ) => {
                setLocalIsFavorite(false);

                handleDeleteFavoriteVariable(variableId, variableName, onCompleted, () => {
                  setLocalIsFavorite(isFavorite);
                  setIsLoading(false);
                });
              },
              handleAddFavoriteVariable: (
                variableId: number,
                variableName: string,
                onCompleted?: () => void
              ) => {
                setLocalIsFavorite(true);

                handleAddFavoriteVariable(variableId, variableName, onCompleted, () => {
                  setLocalIsFavorite(isFavorite);
                  setIsLoading(false);
                });
              },
            }}
          />
        ),
      }}
      content={{
        children: (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {expanded ? (
              <ContentExpandedVariable
                favoriteView={favoriteView}
                fontColor={fontColor}
                graphTrendPreview={graphTrendPreview}
                range={range}
                machineName={machineName}
                value={value}
                borderChipColor={borderChipColor}
                statusText={statusText}
                name={name}
                lastChangeAt={lastChangeAt}
                labelValues={labelValues}
                backgroundColor={backgroundColor}
                status={status}
                textOutdated={textOutdated}
                isMobile={isMobile}
                avgLabel={avgLabel}
                avgValue={avgValue}
                minLabel={minLabel}
                minValue={minValue}
                maxLabel={maxLabel}
                maxValue={maxValue}
                withoutDataLabel={withoutDataLabel}
                isCustomType={isCustomType}
                isMetricOnOff={isMetricOnOff}
                machineStoppedLabel={machineStoppedLabel}
                tags={tags}
                onEditTags={onEditTags}
                editTagsAriaLabel={editTagsAriaLabel}
              />
            ) : (
              <ContentProcessVariable
                favoriteView={favoriteView}
                fontColor={fontColor}
                graphTrendPreview={graphTrendPreview}
                range={range}
                machineName={machineName}
                value={value}
                borderChipColor={borderChipColor}
                statusText={statusText}
                textOutdated={textOutdated}
                lastChangeAt={lastChangeAt}
                status={status}
                isCustomType={isCustomType}
                isMobile={isMobile}
                withoutDataLabel={withoutDataLabel}
                showActionPlanButton={showActionPlanButton}
                actionPlanButtonText={actionPlanButtonText}
                onClickActionPlanButton={onClickActionPlanButton}
                machineStoppedLabel={machineStoppedLabel}
              />
            )}
          </div>
        ),
      }}
    />
  );
};
